import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../Support/with_presenter';
import {UpcomingEventsPresenter} from './UpcomingEventsPresenter';
import {classNames} from '../../Support/classNames';
import {NetworkRequestStatus} from '../../../Network/NetworkResult';
import {addSeconds, format} from 'date-fns';
import {RsvpResponse} from '../../../Models/CalendarEvent';
import {FullCrew} from '../../../Models/Crew';
import {utcToZonedTime} from 'date-fns-tz';
import {Link} from 'react-router-dom';
import {RsvpResponseBar} from '../../Crews/Calendar/Components/RsvpResponseBar/RsvpResponseBar';
import {Loader} from '../Utils/Loader';

interface OwnProps {
    //Limit the upcoming events widget to a set of crews
    crewScopes?: FullCrew[];
    viewAllButton?: boolean;
    placeholder?: boolean;
    className?: string;
}

const UpcomingEventsComponent: FunctionComponent<OwnProps & PresenterProps<UpcomingEventsPresenter>> = observer(
    ({className, viewAllButton, placeholder, presenter}) => {
        if (presenter.upcomingEvent.status === NetworkRequestStatus.Loading) {
            if (placeholder) {
                return (
                    <div className={classNames('card', className)}>
                        <div className="card-title">
                            <h5>Upcoming event</h5>
                        </div>
                        <div className="card-body">
                            <Loader />
                        </div>
                    </div>
                );
            }
            return null;
        }
        if (presenter.upcomingEvent.status === NetworkRequestStatus.Error) {
            return (
                <div className={classNames('card', className)}>
                    <div className="card-title">
                        <h5>Upcoming event</h5>
                    </div>
                    <div className="card-body">
                        <p>There was an error loading your upcoming events.</p>
                        {viewAllButton === true && (
                            <div className="row">
                                <div className="col-12 d-flex justify-content-end">
                                    <Link to="/crews">View all</Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        const calendarEvent = presenter.upcomingEvent.data;
        if (calendarEvent === null) {
            if (placeholder) {
                return (
                    <div className={classNames('card', className)}>
                        <div className="card-title">
                            <h5>Upcoming event</h5>
                        </div>
                        <div className="card-body">
                            <p>No upcoming events</p>
                        </div>
                    </div>
                );
            }
            return null;
        }
        const startAt = utcToZonedTime(calendarEvent.startAt, calendarEvent.timezone);
        return (
            <div className={classNames('card', className)}>
                <div className="card-title">
                    <h5>Upcoming event</h5>
                </div>
                <div className="card-body">
                    <strong>{calendarEvent.title}</strong>
                    <p>
                        {format(startAt, 'EEEE d-M')} {format(startAt, 'H:mm')} -{' '}
                        {format(addSeconds(startAt, calendarEvent.duration.seconds), 'H:mm')}
                    </p>
                    <p>{calendarEvent.description}</p>
                    <div className="d-flex flex-row justify-content-end">
                        <form>
                            <div className="btn-group d-flex" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="rsvp-response"
                                    id="rsvp-yes"
                                    autoComplete="off"
                                    value={RsvpResponse.Yes}
                                    checked={presenter.hasRsvpResponse(RsvpResponse.Yes)}
                                    onChange={() => presenter.onRsvpResponseChange(RsvpResponse.Yes)}
                                />
                                <label className="btn btn-outline-primary w-100" htmlFor="rsvp-yes">
                                    Yes
                                </label>
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="rsvp-response"
                                    id="rsvp-maybe"
                                    autoComplete="off"
                                    value={RsvpResponse.Maybe}
                                    checked={presenter.hasRsvpResponse(RsvpResponse.Maybe)}
                                    onChange={() => presenter.onRsvpResponseChange(RsvpResponse.Maybe)}
                                />
                                <label className="btn btn-outline-primary w-100" htmlFor="rsvp-maybe">
                                    Maybe
                                </label>
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="rsvp-response"
                                    id="rsvp-no"
                                    autoComplete="off"
                                    value={RsvpResponse.No}
                                    checked={presenter.hasRsvpResponse(RsvpResponse.No)}
                                    onChange={() => presenter.onRsvpResponseChange(RsvpResponse.No)}
                                />
                                <label className="btn btn-outline-primary w-100" htmlFor="rsvp-no">
                                    No
                                </label>
                            </div>
                        </form>
                    </div>
                    <RsvpResponseBar className="mt-3" calendarEvent={calendarEvent} />
                    {viewAllButton === true && (
                        <div className="row">
                            <div className="col-12 d-flex mt-3 justify-content-end">
                                <Link to="/crews">View all</Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

export const UpcomingEvents = withPresenter<UpcomingEventsPresenter, OwnProps>(
    (props, component) =>
        new UpcomingEventsPresenter(props.crewScopes || null, component.network.me, component.events, component.rsvp),
    UpcomingEventsComponent,
);
